const decoder = new TextDecoder();

const SIG_CENTRAL = 0x02014b50;
const SIG_EOCD = 0x06054b50;
const MAX_DECOMPRESSED_BYTES = 256 * 1024 * 1024;

export type ArchiveEntry = {
  path: string;
  size: number;
  extract: () => Promise<Uint8Array<ArrayBuffer>>;
};

function inflate(
  data: Uint8Array<ArrayBuffer>,
  format: "deflate-raw" | "gzip"
): Promise<ArrayBuffer> {
  return new Response(
    new Blob([data]).stream().pipeThrough(new DecompressionStream(format))
  ).arrayBuffer();
}

export function listZip(bytes: Uint8Array<ArrayBuffer>): ArchiveEntry[] {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let eocd = bytes.length - 22;
  while (eocd >= 0 && view.getUint32(eocd, true) !== SIG_EOCD) eocd--;
  if (eocd < 0) throw new Error("Not a valid ZIP archive");

  const entries: ArchiveEntry[] = [];
  let pos = view.getUint32(eocd + 16, true);
  for (let n = view.getUint16(eocd + 10, true); n > 0; n--) {
    if (view.getUint32(pos, true) !== SIG_CENTRAL) {
      throw new Error("Malformed ZIP archive");
    }
    const method = view.getUint16(pos + 10, true);
    const compressed = view.getUint32(pos + 20, true);
    const size = view.getUint32(pos + 24, true);
    const nameLen = view.getUint16(pos + 28, true);
    const offset = view.getUint32(pos + 42, true);
    const name = decoder.decode(bytes.subarray(pos + 46, pos + 46 + nameLen));
    pos +=
      46 +
      nameLen +
      view.getUint16(pos + 30, true) +
      view.getUint16(pos + 32, true);

    if (name.endsWith("/")) continue;
    entries.push({
      path: name,
      size,
      extract: async () => {
        const start =
          offset +
          30 +
          view.getUint16(offset + 26, true) +
          view.getUint16(offset + 28, true);
        const data = bytes.subarray(start, start + compressed);
        if (method === 8)
          return new Uint8Array(await inflate(data, "deflate-raw"));
        return data.slice();
      },
    });
  }
  return entries;
}

// Reads a NUL-padded fixed-width tar header field.
function field(block: Uint8Array, off: number, len: number): string {
  const slice = block.subarray(off, off + len);
  const nul = slice.indexOf(0);
  return decoder.decode(nul === -1 ? slice : slice.subarray(0, nul)).trim();
}

// Tars set the header checksum even when the `ustar` magic is empty (old GNU),
// so identify a tar by its checksum rather than the magic.
function isTar(block: Uint8Array): boolean {
  const stored = parseInt(field(block, 148, 8) || "0", 8);
  if (!stored) return false;
  let unsigned = 0;
  let signed = 0;
  for (let i = 0; i < 512; i++) {
    const b = i >= 148 && i < 156 ? 0x20 : block[i]!;
    unsigned += b;
    signed += (b << 24) >> 24;
  }
  return stored === unsigned || stored === signed;
}

// Safe to call on a partially-downloaded buffer: entries whose data hasn't
// fully arrived yet are skipped, and reappear once more bytes are available.
function listTar(bytes: Uint8Array<ArrayBuffer>): ArchiveEntry[] {
  const entries: ArchiveEntry[] = [];
  for (let pos = 0; pos + 512 <= bytes.length;) {
    const block = bytes.subarray(pos, pos + 512);
    if (block.every((b) => b === 0)) break;
    const size = parseInt(field(block, 124, 12) || "0", 8);
    const dataStart = pos + 512;
    const next = dataStart + Math.ceil(size / 512) * 512;
    if (next > bytes.length) break;
    pos = next;
    const isFile = block[156] === 0x30 || block[156] === 0;
    const prefix = field(block, 345, 155);
    const name = field(block, 0, 100);
    const path = isFile ? (prefix ? `${prefix}/${name}` : name) : null;
    if (path) {
      entries.push({
        path,
        size,
        extract: async () =>
          bytes.subarray(dataStart, dataStart + size).slice(),
      });
    }
  }
  return entries;
}

// A gzip stream is either a tar or a single compressed file.
function listGzipPayload(
  bytes: Uint8Array<ArrayBuffer>,
  fileName?: string
): ArchiveEntry[] {
  if (bytes.length >= 512 && isTar(bytes.subarray(0, 512)))
    return listTar(bytes);
  const name = (fileName ?? "file.gz").replace(/\.gz$/i, "") || "file";
  return [{ path: name, size: bytes.length, extract: async () => bytes }];
}

function concat(chunks: Uint8Array[], total: number): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.length;
  }
  return out;
}

// Lists archive entries from a byte stream, re-listing as data arrives so a
// large tar fills in progressively. Tar (optionally gzipped) streams; ZIP is
// buffered, since its central directory is at the end of the file.
export async function streamArchive(
  body: ReadableStream<Uint8Array<ArrayBuffer>>,
  contentType: string,
  fileName: string | undefined,
  onUpdate: (entries: ArchiveEntry[]) => void
): Promise<ArchiveEntry[]> {
  const isGzip =
    contentType === "application/gzip" || contentType === "application/x-gzip";
  if (contentType !== "application/x-tar" && !isGzip) {
    const entries = listZip(
      new Uint8Array(await new Response(body).arrayBuffer())
    );
    onUpdate(entries);
    return entries;
  }

  const reader = (
    isGzip ? body.pipeThrough(new DecompressionStream("gzip")) : body
  ).getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  let parsedAt = 0;
  let entries: ArchiveEntry[] = [];
  for (;;) {
    const { done, value } = await reader.read();
    if (value) {
      chunks.push(value);
      total += value.length;
      if (total > MAX_DECOMPRESSED_BYTES) {
        await reader.cancel();
        throw new Error("Archive is too large to preview");
      }
    }
    if (done || total - parsedAt >= 1 << 19) {
      parsedAt = total;
      const bytes = concat(chunks, total);
      entries = isGzip ? listGzipPayload(bytes, fileName) : listTar(bytes);
      onUpdate(entries);
    }
    if (done) break;
  }
  return entries;
}
